import { Box, Button, ButtonProps, Paper, Stack, alpha } from '@mui/material';
import AuthorizationGate, {
  AuthorizationGateProps,
} from 'components/authorization-gate/authorization-gate.component';
import IncForm from 'components/forms/inc-form/inc-form.component';
import { useMemo, useState } from 'react';
import {
  SubmitErrorHandler,
  SubmitHandler,
  UseFormReturn,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getEnv } from 'util/getEnv';
import DrawerButtonsWrapper from '../drawer-buttons-wrapper/drawer-buttons-wrapper.component';
import UtilityDrawerBody from '../utility-drawer/utility-drawer-body/utility-drawer-body.component';
import UtilityDrawer, {
  UtilityDrawerProps,
} from '../utility-drawer/utility-drawer.component';

export type FormDrawerProps = {
  onValid: SubmitHandler<any>;
  onInvalid?: SubmitErrorHandler<any>;
  cancel?: (e: any) => void;
  onClose: (e: any) => void;
  children: React.ReactNode;
  open: boolean;
  formMethods: UseFormReturn<any>;
  submitButtonProps?: Partial<ButtonProps>;
  submitButtonLabel?: string;
  submitAuthorization?: AuthorizationGateProps;
  disclaimer?: string;
  devTool?: boolean;
  buttons?: (isSubmitting: boolean, isValidating: boolean) => React.ReactNode;
  confirmClose?: boolean;
  footerContent?: React.ReactNode;
} & UtilityDrawerProps;

const FormDrawer = ({
  onValid,
  /* istanbul ignore next */
  onInvalid = getEnv('NODE_ENV') !== 'production'
    ? /* istanbul ignore next */
      e => console.error(e)
    : /* istanbul ignore next */
      () => null,
  cancel,
  onClose,
  children,
  open,
  formMethods,
  submitButtonProps = {},
  submitButtonLabel,
  submitAuthorization = {},
  disclaimer,
  // Off by default — see IncForm: @hookform/devtools corrupts useFieldArray
  // state with our react-hook-form version. Opt in only when debugging.
  devTool = false,
  buttons,
  confirmClose = true,
  footerContent,
  ...rest
}: FormDrawerProps) => {
  const { t } = useTranslation();
  const { isSubmitting, isValidating, dirtyFields } = formMethods?.formState;
  const [confirm, setConfirm] = useState(false);

  const finalOnClose = useMemo(() => cancel ?? onClose, [cancel, onClose]);

  const dirtyCheck = e => {
    if (confirmClose && Object.keys(dirtyFields).length !== 0) {
      setConfirm(true);
    } else {
      finalOnClose(e);
    }
  };

  return (
    <UtilityDrawer open={open} onClose={dirtyCheck} {...rest}>
      {confirm && (
        <Stack
          spacing={2}
          sx={theme => ({
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            zIndex: 1000,
            backgroundColor: alpha(theme.palette.background.paper, 0.5),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(2px)',
            p: 2,
          })}
          data-testid="FormDrawer.CloseConfirmation.Dialog"
        >
          <Stack
            component={Paper}
            variant="outlined"
            sx={theme => ({
              width: '100%',
              p: 4,
              backgroundColor: alpha(theme.palette.background.paper, 0.9),
              alignItems: 'center',
            })}
            spacing={2}
          >
            <Box>{t('forms.closeConfirmationDialog.title')}</Box>
            <Stack direction="row" spacing={2}>
              <Button
                onClick={e => {
                  setConfirm(false);
                  finalOnClose(e);
                }}
                data-testid="FormDrawer.Button.Sure"
              >
                {t('forms.closeConfirmationDialog.buttons.sure')}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setConfirm(false);
                }}
                data-testid="FormDrawer.Button.StayHere"
              >
                {t('forms.closeConfirmationDialog.buttons.stayHere')}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      )}
      <IncForm
        data-testid="FormDrawer.Form"
        display="flex"
        flexDirection="column"
        flexGrow="1"
        justifyContent="space-between"
        overflow="hidden"
        onSubmit={e => {
          // stopPropagation so we don't submit any parent forms
          e.stopPropagation();
          formMethods.handleSubmit(onValid, onInvalid)(e);
        }}
        formMethods={formMethods}
        devTool={devTool}
      >
        <UtilityDrawerBody sx={{ '&&': { pt: 0 } }}>
          {children}
        </UtilityDrawerBody>
        <DrawerButtonsWrapper disclaimer={disclaimer}>
          {footerContent}
          {buttons ? (
            buttons(isSubmitting, isValidating)
          ) : (
            <>
              <AuthorizationGate {...submitAuthorization}>
                <Button
                  type="submit"
                  fullWidth
                  loading={isSubmitting || isValidating}
                  data-testid="FormDrawer.Button.Submit"
                  {...submitButtonProps}
                >
                  {submitButtonLabel ?? t('forms.buttons.save')}
                </Button>
              </AuthorizationGate>
              {cancel && (
                <Button
                  fullWidth
                  onClick={dirtyCheck}
                  variant="outlined"
                  data-testid="FormDrawer.Button.Cancel"
                  disabled={isSubmitting || isValidating}
                >
                  {t('forms.buttons.cancel')}
                </Button>
              )}
            </>
          )}
        </DrawerButtonsWrapper>
      </IncForm>
    </UtilityDrawer>
  );
};
export default FormDrawer;
